import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { WebVoiceSession, type VoiceState } from './voice-session';
import type { RawWidget } from './chat-threads';

/* Shared voice hook for the PRO chats (teacher / admin). Display-only: the voice */
/* token offers render_widget (no interactive, no data tools — those run */
/* server-side over the text path). The student chat keeps its own richer voice */
/* wiring in Chat.tsx (interactive widgets). */
/*  */
/* La TRANSCRIPTION et la CONSOMMATION sont écrites par le proxy WS, qui voyait */
/* déjà passer les deux sans les lire. Ce hook n'écrit plus que les widgets, que */
/* le serveur ne reconstruit pas. Ré-écrire les tours ici les dupliquerait, et */
/* re-déclarer l'usage referait la double facturation corrigée en #519. */

/** @see RawWidget — same shape, re-exported under the local name for callers. */
export type VoiceWidget = RawWidget;
export interface VoiceTurnMsg { role: 'user' | 'assistant'; content: string; widgets?: VoiceWidget[] }

export interface UseWebVoiceOpts {
  threadId: string | null;
  mode: 'general' | 'parcours' | 'teacher' | 'admin';
  /** Recent turns to seed the Live session with, so voice keeps text context. */
  getHistory: () => { role: 'user' | 'model'; text: string }[];
  /** Append freshly-spoken turns / widgets to the page's local message list. */
  onAppend: (msgs: VoiceTurnMsg[]) => void;
  /** The backend resolved (or created) the thread — adopt it so the conversation
   *  is findable again on reload. */
  onThread?: (threadId: string) => void;
  onError?: (msg: string) => void;
}

const REFUSAL_MESSAGES: Record<string, string> = {
  unauthorized: 'Session expirée — reconnecte-toi pour utiliser le micro.',
  forbidden: "Cette conversation ne t'appartient pas.",
  unsupported_mode: 'Le micro n’est pas disponible sur cette page.',
};

export function useWebVoice({ threadId, mode, getHistory, onAppend, onThread, onError }: UseWebVoiceOpts) {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [partialUser, setPartialUser] = useState('');
  const [partialAssistant, setPartialAssistant] = useState('');
  const sessionRef = useRef<WebVoiceSession | null>(null);
  const voiceLive = voiceState !== 'idle' && voiceState !== 'error';

  /* Keep callbacks fresh without re-creating start() on every render. */
  const histRef = useRef(getHistory); histRef.current = getHistory;
  const appendRef = useRef(onAppend); appendRef.current = onAppend;
  const errRef = useRef(onError); errRef.current = onError;
  const onThreadRef = useRef(onThread); onThreadRef.current = onThread;
  const threadRef = useRef(threadId); threadRef.current = threadId;

  const persist = useCallback(async (rows: Record<string, unknown>[]) => {
    const tid = threadRef.current;
    if (!tid || rows.length === 0) return;
    try {
      await api.messages.persistVoice.mutate({
        threadId: tid,
        rows: rows.map((r) => ({ role: String(r.role ?? 'user'), transcript: String(r.transcript ?? ''), created_at: r.created_at as string | undefined })),
      });
    } catch (e) {
      console.warn('[voice] persist failed', e);
    }
  }, []);

  const start = useCallback(async () => {
    if (sessionRef.current) return;
    const initialHistory = histRef.current().slice(-20);
    const session = new WebVoiceSession(
      {
        onState: setVoiceState,
        onError: (e) => errRef.current?.(e.message),
        onReady: ({ threadId: tid }) => {
          threadRef.current = tid;
          onThreadRef.current?.(tid);
        },
        onRefused: (reason) => errRef.current?.(REFUSAL_MESSAGES[reason] ?? 'Micro refusé par le serveur.'),
        onTranscript: (txt, role) => (role === 'user' ? setPartialUser(txt) : setPartialAssistant(txt)),
        onTurnComplete: ({ user, assistant }) => {
          const ui: VoiceTurnMsg[] = [];
          if (user.trim()) ui.push({ role: 'user', content: user });
          if (assistant.trim()) ui.push({ role: 'assistant', content: assistant });
          if (ui.length) appendRef.current(ui);
          setPartialUser('');
          setPartialAssistant('');
        },
        onToolCall: (name, args) => {
          if (name !== 'render_widget') return { error: `unknown tool: ${name}` };
          const a = (args ?? {}) as { html?: string; title?: string };
          const widget: VoiceWidget = { name, html: String(a.html ?? ''), title: (a.title as string) ?? null };
          appendRef.current([{ role: 'assistant', content: '', widgets: [widget] }]);
          /* Persist as metadata.tools so the admin/teacher loaders reconstruct it on reload. */
          void persist([
            { role: 'assistant', transcript: '', created_at: new Date().toISOString(), metadata: { source: 'voice', tools: [{ name, args }] } },
          ]);
          return { ok: true, rendered: true };
        },
      },
      mode,
      threadRef.current,
    );
    sessionRef.current = session;
    await session.start({ initialHistory });
  }, [mode, persist]);

  const stop = useCallback(async () => {
    await sessionRef.current?.stop();
    sessionRef.current = null;
    setPartialUser('');
    setPartialAssistant('');
  }, []);

  const toggle = useCallback(() => {
    if (voiceLive) void stop();
    else void start();
  }, [voiceLive, start, stop]);

  /* Stop the session when the component unmounts. */
  useEffect(() => () => void sessionRef.current?.stop(), []);

  return { voiceState, voiceLive, partialUser, partialAssistant, toggle };
}
